import { useEffect, useState } from "react";
import type { StructFile } from "../data/store";

type State = {
    data: StructFile | null;
    loading: boolean;
    error: string | null;
};

const IDLE: State = { data: null, loading: false, error: null };

/**
 * Fetches one struct file's content lazily (`/api/struct/:name`). Pass `null`
 * when no struct is open. Idempotent under StrictMode via the `ignore` flag.
 */
export function useStruct(name: string | null): State {
    const [state, setState] = useState<State>(IDLE);

    useEffect(() => {
        if (!name) {
            setState(IDLE);
            return;
        }

        let ignore = false;
        setState({ data: null, loading: true, error: null });

        fetch(`/api/struct/${name}`)
            .then((res) => {
                if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
                return res.json() as Promise<StructFile>;
            })
            .then((data) => {
                if (!ignore) setState({ data, loading: false, error: null });
            })
            .catch((err: unknown) => {
                if (!ignore) {
                    const message =
                        err instanceof Error ? err.message : "Failed to load";
                    setState({ data: null, loading: false, error: message });
                }
            });

        return () => {
            ignore = true;
        };
    }, [name]);

    return state;
}
